import { createContext, ReactNode, useContext, useReducer } from "react";
import {
  PositionType,
  WindowManagerAction,
  WindowManagerState,
  WindowModel,
} from "./types";
interface WindowManagerContextType {
  state: WindowManagerState;
  dispatch: React.Dispatch<WindowManagerAction>;
  openWindow: (windowData: Omit<WindowModel, "id" | "zIndex">) => string;
  closeWindow: (id: string) => void;
  updateWindowPosition: (id: string, position: PositionType) => void;
  bringToFront: (id: string) => void;
  toggleMinimise: (id: string) => void;
  closeAllWindows: () => void;
  toggleMaximise: (id: string) => void;
}
const initialState: WindowManagerState = {
  windows: new Map(),
  highestZIndex: 100,
  activeWindowId: null,
};

const windowReducer = (
  state: WindowManagerState,
  action: WindowManagerAction
): WindowManagerState => {
  switch (action.type) {
    case "OPEN_WINDOW": {
      const newWindows = new Map(state.windows);
      const window = action.payload;
      newWindows.set(window.id, window);
      return {
        ...state,
        windows: newWindows,
        highestZIndex: window.zIndex,
        activeWindowId: window.id,
      };
    }
    case "CLOSE_WINDOW": {
      const newWindows = new Map(state.windows);
      newWindows.delete(action.payload);
      return {
        ...state,
        windows: newWindows,
        activeWindowId:
          state.activeWindowId === action.payload ? null : state.activeWindowId,
      };
    }
    case "UPDATE_POSITION": {
      const newWindows = new Map(state.windows);
      const currentWindow = newWindows.get(action.payload.id);
      if (currentWindow) {
        const updatedWindow = {
          ...currentWindow,
          position: action.payload.position,
        };
        newWindows.set(action.payload.id, updatedWindow);
        return { ...state, windows: newWindows };
      }

      return state;
    }
    case "BRING_TO_FRONT": {
      const windows = new Map(state.windows);
      const currentWindow = windows.get(action.payload);
      if (currentWindow) {
        const newHighestZIndex = state.highestZIndex + 1;
        windows.set(action.payload, {
          ...currentWindow,
          zIndex: newHighestZIndex,
        });
        return {
          ...state,
          windows: windows,
          highestZIndex: newHighestZIndex,
          activeWindowId: action.payload,
        };
      }
      return state;
    }
    case "TOGGLE_MINIMISE": {
      const windows = new Map(state.windows);
      const currentWindow = windows.get(action.payload);
      if (currentWindow) {
        windows.set(action.payload, {
          ...currentWindow,
          isMinimized: !currentWindow.isMinimized,
          zIndex: state.highestZIndex + 1,
        });
        return { ...state, windows: windows };
      }
      return state;
    }
    case "TOGGLE_MAXIMISE": {
      const windows = new Map(state.windows);
      const currentWindow = windows.get(action.payload);
      if (currentWindow) {
        const newHighestZIndex = state.highestZIndex + 1;
        windows.set(action.payload, {
          ...currentWindow,
          isMaximized: !currentWindow.isMaximized,
          zIndex: newHighestZIndex,
        });
        return {
          ...state,
          windows,
          highestZIndex: newHighestZIndex,
          activeWindowId: action.payload,
        };
      }
      return state;
    }
    case "CLOSE_ALL": {
      return { ...state, windows: new Map(), activeWindowId: null };
    }

    default:
      return state;
  }
};

//basically need to create context provider then the context to be consumed
//Context

const WindowManagerContext = createContext<
  WindowManagerContextType | undefined
>(undefined);

//Provider Component
export const WindowManagerProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(windowReducer, initialState);
  const openWindow = (
    windowData: Omit<WindowModel, "id" | "zIndex">
  ): string => {
    const id = `window-${Date.now()} - ${Math.random()}}`;
    const newWindow: WindowModel = {
      ...windowData,
      id,
      zIndex: state.highestZIndex + 1,
    };
    dispatch({ type: "OPEN_WINDOW", payload: newWindow });
    return id;
  };

  const closeWindow = (id: string) => {
    dispatch({ type: "CLOSE_WINDOW", payload: id });
  };

  const updateWindowPosition = (id: string, position: PositionType) => {
    dispatch({
      type: "UPDATE_POSITION",
      payload: { id, position },
    });
  };

  const bringToFront = (id: string) => {
    dispatch({ type: "BRING_TO_FRONT", payload: id });
  };

  const toggleMinimise = (id: string) => {
    dispatch({ type: "TOGGLE_MINIMISE", payload: id });
  };

  const toggleMaximise = (id: string) => {
    dispatch({ type: "TOGGLE_MAXIMISE", payload: id });
  };

  const closeAllWindows = () => {
    dispatch({ type: "CLOSE_ALL" });
  };

  const value = {
    state,
    dispatch,
    openWindow,
    closeWindow,
    updateWindowPosition,
    bringToFront,
    toggleMinimise,
    closeAllWindows,
    toggleMaximise,
  };
  return (
    <WindowManagerContext.Provider value={value}>
      {children}
    </WindowManagerContext.Provider>
  );
};

//Hook for consuming
export const useWindowManager = () => {
  const context = useContext(WindowManagerContext);
  if (!context) {
    throw new Error(
      "useWindowManager must be used within WindowManagerProvider"
    );
  }
  return context;
};
